<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="detailedForm">
      <!-- Description Row -->
      <v-row>
        <v-col cols="12">
          <label>{{ $t('Abstract') }}</label>
          <p class="label-info">
            An overview of the patent, technology used, and the benefits it provides.
          </p>
          <tiptap-vuetify
            v-model="patent.patentAbstract"
            :placeholder="'Suggested length is 2000 characters'"
            :extensions="extensions"
            class="required"
            :rules="requiredRule()"
          ></tiptap-vuetify>
          <div :class="abstractCharCount > maxChars ? 'red--text' : 'black--text'">
            {{ abstractCharCount }} / {{ maxChars }} characters
          </div>
        </v-col>
      </v-row>

      <!-- Optional Detailed Info for Edit -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12">
                    <label>{{ $t('description') }}</label>
                    <p class="label-info">
                      A detailed description of the invention, explaining how it works and how it is
                      constructed.
                    </p>
                    <tiptap-vuetify
                      v-model="optionalPatent.description"
                      :placeholder="'Suggested length is 2000 characters'"
                      :extensions="extensions"
                      class="required"
                      :rules="requiredRule()"
                    ></tiptap-vuetify>
                    <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
                      {{ charCount }} / {{ maxChars }} characters
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>Logo/Picture</label>
                    <p class="label-info fixed-label-height">
                      Link to the logo/visual identity of the Patent. The logo will be visible at
                      the Portal. If there is no specific logo for the Patent the logo of the Owner
                      may be used.
                    </p>
                    <v-text-field
                      v-model.trim="optionalPatent.logo"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleLogoInput"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidLogoUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                  <v-col cols="12" md="6" sm="12">
                    <label>Factsheet</label>
                    <p class="label-info fixed-label-height">
                      Upload pdf describing the patents
                    </p>
                    <v-file-input
                      v-model="addFactSheet"
                      outlined
                      :placeholder="'Upload pdf'"
                      clearable
                      prepend-icon=""
                    >
                    </v-file-input>
                    <div v-if="factSheetFile">
                      <v-chip :close="true" @click:close="deleteFactSheet">
                        {{ factSheetFile }}
                      </v-chip>
                    </div>
                    <div align="center">
                      <v-btn
                        @click="uploadFactsheet"
                        :disabled="!isFactSheetSelected"
                        depressed
                        rounded
                        medium
                        color="primary200"
                        class="white--text font-weight-bold"
                        >Upload File
                        <v-icon color="white">mdi-upload</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('related-patents') }}</label>
                    <p class="label-info fixed-label-height">
                      List of other patents that are commonly used with this patent.
                    </p>
                    <input-autocomplete-search
                      v-if="isReady"
                      v-model="patent.relatedPatents"
                      multiple
                      chips
                      label="Related Patents"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      return-object
                      outlined
                      :item-value="'id'"
                      :item-text="'name'"
                      :url="'/api/v1/patent/related-patents/by-name?name='"
                    ></input-autocomplete-search>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('link') }}</label>
                    <p class="label-info fixed-label-height">View on publisher or other website</p>
                    <v-text-field
                      v-model.trim="patent.link"
                      outlined
                      :placeholder="'Insert a valid URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleInput"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row
                  v-for="(useCase, index) in optionalPatent.useCases"
                  :key="index"
                  align="center"
                  class="mt-0 pt-0"
                >
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('use-case') }}</label>
                    <p class="label-info">
                      {{ $t('equipment-user-cases-info') }}
                    </p>
                    <v-text-field
                      v-model.trim="optionalPatent.useCases[index]"
                      outlined
                      :rules="[
                        v => !v || (v.length >= 4 && v.length <= 500) || 'Must be 4-500 characters',
                      ]"
                      :placeholder="'Use Case'"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col class="mt-5" v-if="optionalPatent.useCases.length > 1">
                    <v-btn fab small text @click="removeUseCase(index)">
                      <v-icon>
                        $vuetify.icons.trash
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <!-- Conditionally render the Add Use Case button -->
                <v-row v-if="hasNonEmptyUseCases" class="mt-0 pt-0">
                  <v-col cols="4">
                    <v-btn
                      @click="addUseCase"
                      depressed
                      rounded
                      large
                      color="primary200"
                      class="white--text font-weight-bold"
                    >
                      <v-icon left>
                        $vuetify.icons.plus
                      </v-icon>
                      <span class="text-decoration-underline text-transform-none font-weight-bold">
                        {{ $t('add-use-case') }}
                      </span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars || abstractCharCount > maxChars"
          v-if="unsavedChanges && edit"
          @click="updatePatentDetailedInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars || abstractCharCount > maxChars"
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import InputAutocompleteSearch from '@/components/shared/inputs/InputAutocompleteSearch';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Strike,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'PatentDetailedInfo',
  mixins: [util],
  components: { TiptapVuetify, AlertUnsavedChanges, InputAutocompleteSearch },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    patent: {
      patentAbstract: '',
      link: '',
      relatedPatents: [],
    },
    optionalPatent: {
      description: '',
      logo: '',
      useCases: [''],
    },
    addFactSheet: null,
    isFactSheetSelected: false,
    factSheetFile: null,
    status: false,
    initialPatent: {},
    initialStatus: {},
    initialOptionalPatent: {},
    dataLoaded: false,
    isValidUrl: true,
    initialUseCases: [],
    unsavedChanges: false,
    isValidLogoUrl: true,
    extensions: [Bold, Italic, Underline, Strike, ListItem, BulletList, OrderedList],
    isReady: false,
    maxChars: 2000,
  }),
  watch: {
    patent: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;

        const abstractChanged = this.patent.patentAbstract !== this.initialPatent.patentAbstract;
        const linkChanged = this.patent.link !== this.initialPatent.link;
        const relatedPatentsChanged =
          JSON.stringify(this.patent.relatedPatents) !==
          JSON.stringify(this.initialPatent.relatedPatents);

        this.unsavedChanges = abstractChanged || linkChanged || relatedPatentsChanged;
      },
    },
    optionalPatent: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalPatent.description !== this.initialOptionalPatent.description ||
          this.optionalPatent.logo !== this.initialOptionalPatent.logo;
      },
    },
    'optionalPatent.useCases': {
      deep: true,
      handler(newUsecase) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newUsecase) !== JSON.stringify(this.initialUseCases)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
    addFactSheet(newVal) {
      this.isFactSheetSelected = newVal !== null;
    },
  },
  mounted() {
    setTimeout(() => {
      this.isReady = true;
    }, 500);

    if (this.edit) {
      this.getPatentIdDetailedInfo();
    }
  },
  computed: {
    hasNonEmptyUseCases() {
      return this.optionalPatent.useCases.some(useCase => useCase && useCase.trim() !== '');
    },
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.optionalPatent.description || '';
      return div.textContent.length;
    },
    abstractCharCount() {
      const div = document.createElement('div');
      div.innerHTML = this.patent.patentAbstract || '';
      return div.textContent.length;
    },
  },
  methods: {
    next() {
      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid && this.patent.patentAbstract) {
        this.$emit('tab', 2);
        this.$emit('patentDetailedInfo', { ...this.patent });
      }
    },
    back() {
      this.$emit('tab', 0);
    },
    handleInput(value) {
      if (value) {
        this.validateUrl(value);
      } else {
        this.isValidUrl = true;
      }
    },
    validateUrl() {
      this.isValidUrl = this.canParse(this.patent.link);
    },
    handleLogoInput(value) {
      if (value) {
        this.validateLogoUrl(value);
      } else {
        this.isValidLogoUrl = true;
      }
    },
    validateLogoUrl() {
      this.isValidLogoUrl = this.canParse(this.optionalPatent.logo);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
    addUseCase() {
      this.optionalPatent.useCases.push('');
    },
    removeUseCase(index) {
      this.optionalPatent.useCases.splice(index, 1);
    },
    getPatentIdDetailedInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/patent/${id}`)
        .then(response => {
          console.log('Get Patent Detailed Info', response.data);

          this.status = response.data.published;
          this.patent.patentAbstract = response.data.patentAbstract;
          this.patent.relatedPatents = response.data.relatedPatents;
          this.patent.link = response.data.link;
          this.optionalPatent.description = response.data.description;
          this.optionalPatent.logo = response.data.logo;
          if (response.data.useCases.length > 0) {
            this.optionalPatent.useCases = response.data.useCases || [''];

            this.initialUseCases = JSON.parse(JSON.stringify(this.optionalPatent.useCases));
          } else {
            this.$set(this.optionalPatent, 'useCases', ['']);
            this.initialUseCases = JSON.parse(JSON.stringify(this.optionalPatent.useCases));
          }

          if (response.data.factsheet) {
            this.factSheetFile = response.data.factsheet;
          }

          this.initialPatent = {
            patentAbstract: response.data.patentAbstract,
            link: response.data.link,
            relatedPatents: [...response.data.relatedPatents],
          };

          this.initialOptionalPatent = {
            description: response.data.description,
            logo: response.data.logo,
          };

          this.initialStatus = {
            published: response.data.published,
          };
          this.$emit('status', this.initialStatus.published);
          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching patent data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.getPatentIdDetailedInfo();
      this.optionalPatent.useCases = this.initialUseCases;
    },
    updatePatentDetailedInfo() {
      const id = this.$route.params.id;
      const patentUpdateData = {
        published: this.published,
        id: id,
        link: this.patent.link,
        useCases: this.optionalPatent.useCases,
        patentAbstract: this.patent.patentAbstract,
        description: this.optionalPatent.description,
        logo: this.optionalPatent.logo,
        relatedPatents: [...this.patent.relatedPatents],
      };

      this.deleteEmptyProperties(patentUpdateData);

      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid) {
        this.axios
          .put('api/v1/patent/detailed-info', patentUpdateData)
          .then(response => {
            console.log('Patent info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Patent Updated Successfully',
            });
            this.initialPatent = { ...this.patent };
            this.initialOptionalPatent = { ...this.optionalPatent };
            this.initialStatus = this.status;
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);

            this.getPatentIdDetailedInfo();
          })
          .catch(error => {
            console.error('Error updating patent info:', error);
          });
      }
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    uploadFactsheet() {
      const id = this.$route.params.id;

      if (this.addFactSheet) {
        const fileType = this.addFactSheet.type;
        if (fileType !== 'application/pdf') {
          EventBus.$emit('snackbar', {
            text: 'Only PDF files are allowed!',
          });
          return;
        }

        const formData = new FormData();
        formData.append('type', this.addFactSheet.type);
        formData.append('name', this.addFactSheet.name);
        formData.append('size', this.addFactSheet.size);
        formData.append('file', this.addFactSheet);

        this.axios
          .put(`api/v1/patent/${id}/file?field=factSheet`, formData)
          .then(response => {
            console.log('Response Add File:', response.data);
            EventBus.$emit('snackbar', {
              text: 'File uploaded successfully!',
            });
            this.getPatentIdDetailedInfo();
          })
          .catch(error => {
            console.log('Error:', error);
            EventBus.$emit('snackbar', {
              text: 'Failed to upload file. Please try again.',
            });
          });
      } else {
        console.log('Invalid file or id');
      }
    },
    deleteFactSheet() {
      const id = this.$route.params.id;

      if (this.factSheetFile) {
        const fileName = this.factSheetFile.name;

        this.axios
          .put(`api/v1/patent/${id}/file/${fileName}?field=factSheet`)
          .then(response => {
            console.log('Factsheet deleted successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Factsheet deleted successfully!',
            });
            this.factSheetFile = null;
            this.addFactSheet = null;
          })
          .catch(error => {
            console.error('Error deleting Factsheet:', error);
            EventBus.$emit('snackbar', {
              text: 'Failed to delete Factsheet. Please try again.',
            });
          });
      } else {
        console.log('No Factsheet File to delete.');
      }
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 4em;
}
</style>
